import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { Bath, Zap, Droplets, Home, Hammer, Grid3X3, TreeDeciduous, Wrench, Paintbrush, Waves, LucideIcon } from "lucide-react";
interface Trade {
  icon: LucideIcon;
  title: string;
  alt: string;
}
const trades: Trade[] = [{
  icon: Bath,
  title: "Kitchen & Bathroom Renovations",
  alt: "Marketing for kitchen and bathroom renovation businesses in Australia"
}, {
  icon: Zap,
  title: "Electricians",
  alt: "Digital marketing for electricians and electrical contractors Australia"
}, {
  icon: Droplets,
  title: "Plumbers & Drainage",
  alt: "Marketing for plumbers and drainage specialists across Australia"
}, {
  icon: Home,
  title: "Roofers & Guttering",
  alt: "Digital marketing for roofing contractors and gutter specialists"
}, {
  icon: Hammer,
  title: "Builders & Carpenters",
  alt: "Marketing for builders, carpenters and construction companies Australia"
}, {
  icon: Grid3X3,
  title: "Flooring & Tiling",
  alt: "Marketing for flooring installers and tilers Australia"
}, {
  icon: TreeDeciduous,
  title: "Landscapers",
  alt: "Digital marketing for landscaping and garden design businesses"
}, {
  icon: Wrench,
  title: "Remedial Builders",
  alt: "Marketing for remedial building and repair contractors"
}, {
  icon: Paintbrush,
  title: "Painters & Renderers",
  alt: "Marketing for painters, renderers and decorators Australia"
}, {
  icon: Waves,
  title: "Pool Builders",
  alt: "Digital marketing for pool builders and fibreglass installers"
}];
function TradeCard({
  trade
}: {
  trade: Trade;
}) {
  const Icon = trade.icon;
  return <article className="group flex flex-col items-center text-center p-4 sm:p-6 rounded-xl bg-card border border-border/50 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1 h-full">
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-primary/20 transition-colors duration-300" aria-hidden="true">
        <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" strokeWidth={1.5} />
      </div>
      <h3 className="font-semibold text-foreground text-[0.9375rem] sm:text-base leading-tight">
        {trade.title}
      </h3>
    </article>;
}
export function TradesSection() {
  const [emblaRef] = useEmblaCarousel({
    loop: true,
    align: "start",
    dragFree: true
  }, [AutoScroll({
    playOnInit: true,
    speed: 0.6,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
    direction: 'backward'
  })]);
  return <section className="section-padding bg-muted/30 relative overflow-hidden" aria-labelledby="trades-heading">
      {/* Background accents */}
      <div className="absolute top-0 left-0 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-primary/[0.02] rounded-full blur-[80px] sm:blur-[100px] -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute inset-0 grid-pattern opacity-20" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-80px"
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="max-w-3xl mx-auto text-center mb-8 sm:mb-10">
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>Industries We Serve</span>
          </div>
          <h2 id="trades-heading" className="heading-section text-foreground mb-3 sm:mb-4 text-balance">
            Digital Marketing for{" "}
            <span className="text-gradient">Australian Tradies</span>
          </h2>
          <p className="text-[0.9375rem] sm:text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">We build digital growth systems tailored specifically for trade, construction and home service businesses across Sydney, Melbourne, Brisbane, Perth and Australia-wide. From local SEO to Paid Ads, we help you dominate your local market.</p>
        </motion.div>

        {/* Trades Carousel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }}>
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-3 sm:gap-4">
              {trades.map((trade, index) => <div key={index} className="flex-none w-[45%] sm:w-[30%] md:w-[22%] lg:w-[18%]">
                  <TradeCard trade={trade} />
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Additional Industries Text */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="text-center text-muted-foreground text-base mt-6">
          Plus: HVAC, Fencing, Concrete, Demolition, Solar, Security Systems & more home service businesses.
        </motion.p>
      </div>
    </section>;
}